import { css, keyframes, styled } from 'styled-components';

import { colors, transition } from '#/theme/theme';

export const fadeIn = keyframes`
  from {
    fill-opacity: 0;
  }
  to {
    fill-opacity: 1;
  }
`;

export const dash = keyframes`
    from {
      stroke-dashoffset: 1000;
    }

    to {
      stroke-dashoffset: 0;
    }
`;

export const TooltipContainer = styled.div`
  text-align: center;
`;

export const StyledSvg = styled.svg<{
  $active: boolean;
  $initialized: boolean;
  $overrideZIndex?: number;
  $loading: boolean;
  $height?: number;
}>`
  height: ${(props) => props.$height}px;
  overflow: visible;
  left: 0;
  position: absolute;
  z-index: ${(props) => (props.$active ? 101 : 1)};

  ${(props) =>
    props.$overrideZIndex
      ? `
      z-index: ${props.$overrideZIndex};
    `
      : undefined}

  rect {
    fill: ${colors.white};
    pointer-events: ${(props) =>
      !props.$initialized || props.$loading ? 'none' : 'all'};
    stroke: ${colors.brandBlue};
    stroke-width: 3px;
  }
`;

export const MainLine = styled.path<{
  $fill?: string;
  $hoverFill?: string;
  $initialized: boolean;
  $active: boolean;
  $loading: boolean;
  $stroke?: string;
}>`
  pointer-events: all;
  fill: ${(props) => {
    if (props.$fill) {
      return props.$initialized && props.$active && !props.$loading
        ? props.$fill
        : colors.calloutGrey;
    }

    return 'none';
  }};
  stroke: ${(props) => {
    if (props.$stroke) {
      if (!props.$initialized || props.$loading) {
        return colors.calloutGrey;
      }

      return props.$stroke;
    }

    return 'none';
  }};
  stroke-width: 4px;
  stroke-dasharray: 1000;
  stroke-dashoffset: 1000;
  transition:
    fill ${transition},
    fill-opacity 0.2s ease-in;
  ${(props) =>
    props.$loading
      ? css`
          animation:
            ${dash} 1.5s linear forwards,
            ${fadeIn} 0.5s ease-out forwards;
        `
      : 'animation: none'};

  & + svg {
    overflow: visible !important;

    & > foreignObject {
      overflow: visible !important;

      > div {
        transition: all 0.2s ease-in;
        transform: translateY(0);
      }
    }
  }

  &:hover + svg > foreignObject > div {
    opacity: 1;
    transform: translateY(-25px);
  }

  &:hover + g > path {
    ${(props) => (props.$hoverFill ? `fill: ${props.$hoverFill};` : undefined)}
    fill-opacity: 1;
  }
`;

export const CustomTooltip = styled.div<{ $direction: string }>`
  opacity: 0;
  padding: 5px 8px;
  background: white;
  border: 1px solid ${colors.brandBlue};
  box-shadow: 0 4px 9px rgb(0 0 0 / 0.1);
  border-radius: 12px;
  color: ${colors.lightContentGrey};
  white-space: nowrap;
  transition: transform ${transition};

  &::after {
    content: '';
    position: absolute;
    display: block;
    width: 12px;
    height: 12px;
    background: white;

    ${(props) => {
      if (props.$direction === 'right') {
        return `
          border-bottom: 1px solid ${colors.brandBlue};
          border-left: 1px solid ${colors.brandBlue};
          left: -11px;
          top: 50%;
          transform: translateX(4px) translateY(-50%) rotate(45deg);`;
      }

      if (props.$direction === 'up') {
        return `
          border-bottom: 1px solid ${colors.brandBlue};
          border-right: 1px solid ${colors.brandBlue};
          bottom: -3px;
          margin: auto;
          left: 0;
          right: 0;
          transform: translateY(4px) rotate(45deg);`;
      }

      return `
          border-top: 1px solid ${colors.brandBlue};
          border-right: 1px solid ${colors.brandBlue};
          right: -11px;
          top: 50%;
          transform: translateX(-4px) translateY(-50%) rotate(45deg);
          `;
    }}
  }
`;

export const StyledRect = styled.rect`
  opacity: 0;

  & + svg {
    overflow: visible;

    foreignObject {
      overflow: visible;
    }
  }

  &:hover {
    opacity: 1;
  }

  &:hover + svg > foreignObject > div {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const HoverSectionContainer = styled.g`
  pointer-events: all;
`;

export const HoverSection = styled.path<{
  $active?: boolean;
  $hoverFill?: string;
}>`
  fill-opacity: ${(props) => (props.$active ? 1 : 0)};
  stroke: none;

  ${(props) =>
    props.$hoverFill && props.$active
      ? `fill: ${props.$hoverFill};`
      : undefined}

  &:hover {
    ${(props) => (props.$hoverFill ? `fill: ${props.$hoverFill};` : undefined)}
    fill-opacity: 1;
  }
`;

export const HoverSectionTooltip = styled.div<{ $active?: boolean }>`
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.1s ease-in;

  ${(props) =>
    props.$active
      ? `
    opacity: 1;
    transform: translateY(0);
    transition-delay: 0.2s;
  `
      : undefined}
`;
